import { useEffect, useState, useCallback } from 'react'
import type { MultiValue } from 'react-select'

import {
  IUseDownloads,
  IPowerPlantSelectOption,
  IPowerPlantSystemSelectAllOption,
  IPowerPlantSystemGroupedByTypeOfEquipment,
  IPowerPlantSystemGroupedOption,
  DownloadsData,
} from '../../../domain/downloads.types'
import { logger } from '../../../utils/logger'
import {
  formattedPowerPlantSystemsForSelectInput,
  powerPlantSystemsGroupedOption,
  formatDownloadDate,
  formattedSystemsForPostRequest,
  flattenSystemsOptions,
  addSelectAllOptionToPowerPlantSystemsOptions,
  sortPowerPlantInverterSystemsByPositionNumber,
} from './downloads.utils'
import {
  NUMBER_OF_DAYS_PER_MONTH,
  SELECT_ALL_OPTION_VALUE,
} from '../../../constants/downloads.constants'
import { useDownloadsUseCase } from '../../../application/useCases/useDownloads'
import { useStore } from '../../../application/store/store'
import { useGetAllUserPlantsUseCase } from '../../../application/useCases/usePlantSelect'

export const useDownloads = (): IUseDownloads => {
  const { plants, setAllUserPlants, downloadLoading } = useStore()
  const { getAllUserPlants } = useGetAllUserPlantsUseCase()
  const { getPowerPlantSystems, exportPowerPlantSystemsData } = useDownloadsUseCase()
  const userAccessState = useStore((state) => state.user.access)
  const [selectedPowerPlantId, setSelectedPowerPlantId] = useState<number>(0)
  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([null, null])
  const [startDate, endDate] = dateRange
  const [maxDate, setMaxDate] = useState<Date>(new Date())
  const [powerPlantSystemsOptions, setPowerPlantSystemsOptions] = useState<
    IPowerPlantSystemGroupedOption[]
  >([])
  const [selectedPowerPlantSystems, setSelectedPowerPlantSystems] = useState<
    IPowerPlantSystemSelectAllOption[] | null
  >(null)
  const [isSubmitButtondisabled, setIsSubmitButtonDisabled] = useState(true)
  const [isDownloading, setIsDownloading] = useState<boolean>(false)

  const powerPlantListSorted = plants.map((plant) => ({
    id: plant.code_centrale,
    label: plant.nom,
  }))

  const fetchAllUserPlants = useCallback(async () => {
    const data = await getAllUserPlants()
    data && setAllUserPlants(data)
  }, [])

  useEffect(() => {
    if (userAccessState?.downloadPage) {
      fetchAllUserPlants()
    }
  }, [userAccessState?.downloadPage])

  /**
   * manage the submit button disable state
   */
  const handleIsSubmitButtonDisabled = useCallback(() => {
    setIsSubmitButtonDisabled(selectedPowerPlantId === 0 || !startDate || !endDate)
  }, [startDate, endDate, selectedPowerPlantId])

  /**
   * when user select a different power plant, reset the selected power plant systems
   * @param powerPlantOption
   */
  const handlePowerPlantChange = (powerPlantOption: IPowerPlantSelectOption) => {
    setSelectedPowerPlantId(powerPlantOption.id)
    setSelectedPowerPlantSystems(null)
  }

  /**
   * use in the datePicker, when user select a start date, set,for
   * the end date,  the max date available at 30 days after the start date
   * or at today if there is less than 30 days between the start date and today
   */
  const handleOnSelectStartDate = (startingDate: Date) => {
    const newMaxDate = new Date(startingDate)
    newMaxDate.setDate(newMaxDate.getDate() + NUMBER_OF_DAYS_PER_MONTH)
    if (newMaxDate < new Date()) {
      setMaxDate(newMaxDate)
    }
  }

  /**
   * each time a user select a system, it map the selected systems and
   * set the selectedPowerPlantSystems state
   * @param systems
   */
  const handleSystemsChange = (
    systemsSelectedOptions: MultiValue<IPowerPlantSystemSelectAllOption>
  ) => {
    const allSystemsOptions: IPowerPlantSystemSelectAllOption[] = powerPlantSystemsOptions
      ? [...flattenSystemsOptions(powerPlantSystemsOptions)]
      : []

    if (
      systemsSelectedOptions &&
      systemsSelectedOptions.some(
        (option: IPowerPlantSystemSelectAllOption) => option.value === SELECT_ALL_OPTION_VALUE
      )
    ) {
      setSelectedPowerPlantSystems(allSystemsOptions)
    } else {
      const selectedSystemsOptions = systemsSelectedOptions.map((system) => system)
      setSelectedPowerPlantSystems(selectedSystemsOptions)
    }
  }

  /**
   * On Submit fetch data : zip containing an .xlsx file with the systems data
   * zip download by creating a link, removed after usage
   */
  const handleOnSubmit = async () => {
    setIsDownloading(true)
    setIsSubmitButtonDisabled(true)
    const systemsData = await exportPowerPlantSystemsData(
      selectedPowerPlantId,
      formatDownloadDate(startDate as Date),
      formatDownloadDate(endDate as Date),
      formattedSystemsForPostRequest(selectedPowerPlantSystems)
    )

    try {
      const href = URL.createObjectURL(systemsData)
      const link = document.createElement('a')
      link.href = href
      link.setAttribute(
        'download',
        `${selectedPowerPlantId}_${formatDownloadDate(startDate as Date)}_${formatDownloadDate(
          endDate as Date
        )}.zip`
      )
      link.click()
      URL.revokeObjectURL(href)
      setIsDownloading(false)
      setIsSubmitButtonDisabled(false)
    } catch (error: any) {
      logger.error(error.message)
    }
  }

  useEffect(() => {
    if (selectedPowerPlantId !== 0) {
      const powerPlantsSystems = async () => {
        try {
          const response: DownloadsData[] = await getPowerPlantSystems(selectedPowerPlantId)
          const selectedPowerPlantSystemsFormatted: IPowerPlantSystemGroupedByTypeOfEquipment = formattedPowerPlantSystemsForSelectInput(
            response
          )

          const groupedPowerPlantSystems = powerPlantSystemsGroupedOption(
            selectedPowerPlantSystemsFormatted
          )

          const groupedPowerPlantSystemsSortedByPositionNumber = sortPowerPlantInverterSystemsByPositionNumber(
            groupedPowerPlantSystems
          )

          const groupedPowerPlantSystemsWithSelectAllOption = addSelectAllOptionToPowerPlantSystemsOptions(
            groupedPowerPlantSystemsSortedByPositionNumber
          )

          setPowerPlantSystemsOptions(groupedPowerPlantSystemsWithSelectAllOption)
        } catch (error) {
          logger.error((error as Error).message)
          throw new Error((error as Error).message)
        }
      }
      powerPlantsSystems()
    }
    handleIsSubmitButtonDisabled()
  }, [selectedPowerPlantId, dateRange])

  return {
    downloadLoading,
    powerPlantListSorted,
    startDate,
    endDate,
    maxDate,
    powerPlantSystemsOptions,
    selectedPowerPlantSystems,
    isSubmitButtondisabled,
    isDownloading,
    userAccessState,
    handlePowerPlantChange,
    setDateRange,
    setMaxDate,
    handleOnSelectStartDate,
    handleSystemsChange,
    handleOnSubmit,
  }
}
